// skipcq: JS-W1028
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import useBeerActivityByStateDailyReports from '../../hooks/useBeerActivityByStateDailyReports';
import { capturePostHogEvent } from '../../utils/posthogClient';
import {
  BEER_ACTIVITY_MAP_METRICS,
  readByStateMetrics,
  readSummaryComparison,
} from '../../utils/beerActivityByStateReport';
import BeerActivityByStateMap from './BeerActivityByStateMap';
import BeerActivityByStateTable from './BeerActivityByStateTable';
import DashboardMetricPanel from './DashboardMetricPanel';

/**
 * @param {number} value
 * @returns {string}
 */
function formatAverage(value) {
  if (!Number.isFinite(value)) {
    return '0.00';
  }
  return value.toFixed(2);
}

function BeerActivityByStateCard() {
  const { latestSnapshot, loading, error } = useBeerActivityByStateDailyReports();
  const [metricKey, setMetricKey] = useState(BEER_ACTIVITY_MAP_METRICS.reviews);
  const [tableVisible, setTableVisible] = useState(false);

  useEffect(() => {
    capturePostHogEvent('engage-view-beer-activity-by-state');
  }, []);

  const metrics = useMemo(
    () => latestSnapshot?.metrics ?? {},
    [latestSnapshot],
  );
  const byState = useMemo(() => readByStateMetrics(metrics), [metrics]);
  const summary = useMemo(() => readSummaryComparison(metrics), [metrics]);
  const snapshotDate =
    typeof metrics.snapshotDate === 'string' ? metrics.snapshotDate : latestSnapshot?.date;
  const windowDays =
    Number.isFinite(metrics.windowDays) && metrics.windowDays > 0
      ? metrics.windowDays
      : 60;

  const handleMetricChange = useCallback((_event, nextMetric) => {
    if (nextMetric !== null) {
      setMetricKey(nextMetric);
    }
  }, []);

  const handleToggleTable = useCallback(() => {
    setTableVisible((previous) => !previous);
  }, []);

  const infoContent = (
    <>
      Do states with beer in the database see more review and logging activity per active
      user-day?
      {latestSnapshot ? (
        <>
          {' '}
          Trailing {windowDays}-day window · snapshot {snapshotDate ?? 'unknown'} · US Eastern ·
          brewery-state attribution · beer reviews and beer log entries only · from{' '}
          <code>beerActivityByStateReports</code>.
        </>
      ) : null}
    </>
  );

  const panelContent = useMemo(() => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }

    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }

    if (!latestSnapshot) {
      return (
        <Typography variant="body2" color="text.secondary">
          No beer activity by state reports yet. Run the backend reporter or backfill script.
        </Typography>
      );
    }

    return (
      <>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Weighted avg activities per active user-day — states with beer in DB:{' '}
          <strong>{formatAverage(summary.withBeer.avgActivitiesPerActiveUserDay)}</strong>
          ({summary.withBeer.activeUserDays} user-days) · without beer in DB:{' '}
          <strong>{formatAverage(summary.withoutBeer.avgActivitiesPerActiveUserDay)}</strong>
          ({summary.withoutBeer.activeUserDays} user-days)
        </Typography>

        <ToggleButtonGroup
          value={metricKey}
          exclusive
          onChange={handleMetricChange}
          size="small"
          sx={{ mb: 2 }}
          aria-label="Map metric"
        >
          <ToggleButton value={BEER_ACTIVITY_MAP_METRICS.reviews}>Reviews</ToggleButton>
          <ToggleButton value={BEER_ACTIVITY_MAP_METRICS.checkins}>Log entries</ToggleButton>
          <ToggleButton value={BEER_ACTIVITY_MAP_METRICS.avgPerUserDay}>
            Avg / user-day
          </ToggleButton>
        </ToggleButtonGroup>

        <BeerActivityByStateMap byState={byState} metricKey={metricKey} />

        <Link
          component="button"
          variant="body2"
          onClick={handleToggleTable}
          aria-expanded={tableVisible}
          sx={{ mt: 2, display: 'inline-block' }}
        >
          {tableVisible ? 'Hide detail table' : 'Show detail table'}
        </Link>

        {tableVisible ? (
          <Box sx={{ mt: 2 }}>
            <BeerActivityByStateTable byState={byState} />
          </Box>
        ) : null}
      </>
    );
  }, [
    byState,
    error,
    handleMetricChange,
    handleToggleTable,
    latestSnapshot,
    loading,
    metricKey,
    summary,
    tableVisible,
  ]);

  return (
    <DashboardMetricPanel title="Beer activity by state" infoContent={infoContent}>
      {panelContent}
    </DashboardMetricPanel>
  );
}

export default BeerActivityByStateCard;
